import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

interface GroupSchedule {
  id: number;
  dayOfWeek: string;
  time: string;
}

interface Group {
  id: number;
  name: string | null;
  level: string;
  startDate: string;
  endDate?: string | null;
  offerNumber?: number | null;
  schedules: GroupSchedule[];
  parentGroupId?: number | null;
  parentGroup?: {
    id: number;
    name: string | null;
    level: string;
    startDate: string;
    endDate?: string | null;
  } | null;
  hiddenFromLanding?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface GroupsState {
  groups: Group[];
  currentGroup: Group | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  totals: {
    active: number;
    finished: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: GroupsState = {
  groups: [],
  currentGroup: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  totals: {
    active: 0,
    finished: 0,
  },
  loading: false,
  error: null,
};

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks
export const fetchGroupsAsync = createAsyncThunk(
  'groups/fetchGroups',
  async (
    { page = 1, limit = 10, level, status }: { page?: number; limit?: number; level?: string; status?: 'active' | 'finished' } = {},
    { rejectWithValue, getState }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (level) params.append('level', level);
      if (status) params.append('status', status);

      const response = await axios.get(`${API_BASE_URL}/groups?${params.toString()}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch groups'));
    }
  }
);

export const fetchGroupByIdAsync = createAsyncThunk(
  'groups/fetchGroupById',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/groups/${id}`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.group;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch group'));
    }
  }
);

export const createGroupAsync = createAsyncThunk(
  'groups/createGroup',
  async (groupData: {
    name?: string;
    level: string;
    startDate: string;
    endDate?: string; // optional
    schedules: { dayOfWeek: string; time: string }[];
    offerNumber?: number;
    parentGroupId?: number;
  }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/groups`, groupData, {
        headers: getAuthHeaders(getState)
      });
      return response.data; // Return full response including message and attachedStudentsCount
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create group'));
    }
  }
);

export const updateGroupAsync = createAsyncThunk(
  'groups/updateGroup',
  async ({ id, groupData }: {
    id: number;
    groupData: {
      name?: string;
      level?: string;
      startDate?: string;
      endDate?: string | null; // allow null to clear
      schedules?: { dayOfWeek: string; time: string }[];
      offerNumber?: number | null;
      parentGroupId?: number | null;
    };
  }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/groups/${id}`, groupData, {
        headers: getAuthHeaders(getState)
      });
      return response.data; // Return full response including message and attachedStudentsCount
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update group'));
    }
  }
);

export const deleteGroupAsync = createAsyncThunk(
  'groups/deleteGroup',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(`${API_BASE_URL}/groups/${id}`, {
        headers: getAuthHeaders(getState)
      });
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete group'));
    }
  }
);

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch groups
      .addCase(fetchGroupsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.groups;
        state.pagination = action.payload.pagination;
        if (action.payload.totals) {
          state.totals = action.payload.totals;
        } else {
          state.totals = { active: 0, finished: 0 };
        }
        state.error = null;
      })
      .addCase(fetchGroupsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch group by ID
      .addCase(fetchGroupByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGroup = action.payload;
        state.error = null;
      })
      .addCase(fetchGroupByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create group
      .addCase(createGroupAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGroupAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.groups.unshift(action.payload.group);
        state.error = null;
      })
      .addCase(createGroupAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update group
      .addCase(updateGroupAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGroupAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGroup = action.payload.group;
        const index = state.groups.findIndex(group => group.id === action.payload.group.id);
        if (index !== -1) {
          state.groups[index] = action.payload.group;
        }
        state.error = null;
      })
      .addCase(updateGroupAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete group
      .addCase(deleteGroupAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGroupAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = state.groups.filter(group => group.id !== action.payload);
        if (state.currentGroup?.id === action.payload) {
          state.currentGroup = null;
        }
        state.error = null;
      })
      .addCase(deleteGroupAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentGroup } = groupsSlice.actions;
export default groupsSlice.reducer; 